import { useRef, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, useParams, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";

// Auth Context
import { AuthProvider, useAuth } from "@/contexts/AuthContext";

// Shared Components
import Navbar from "@/components/shared/Navbar";
import ErrorBoundary from "@/components/shared/ErrorBoundary";

// Public Pages
import HomePage from "@/pages/public/HomePage";
import { PricingPage, FeaturesPage, SolutionsPage, ContactPage, SavingsPage, TermsPage, PrivacyPage, MethodologyPage } from "@/pages/public";
import AccountTermsPage from "@/pages/public/AccountTermsPage";

// Auth Pages
import { LoginPage, RegisterPage } from "@/pages/auth";

// Core Pages
import { DashboardPage, ToolsPage, ReportsListPage, ReportViewPage } from "@/pages/core";

// Platform Pages
import {
  PlatformDashboardPage, PlatformCasesPage, PlatformProfilesPage,
  PlatformSettlementsPage, PlatformBillingPage, PlatformAdminPage,
  PlatformDocumentsPage, PlatformApplicationsPage
} from "@/pages/platform";

// Tool Pages - Core
import { 
  LitixWizardPage, InvestigatorPage, LytixLedgerPage, StrategyPatternPage,
  CaseBuilderPage, StateBarPage, CaseIntelligencePage, LegalDataHubPage,
  PrecedentFinderPage, TrendAnalyzerPage, SecretsManagerPage, ClientIntakePage,
  WatchlistPage, AttorneyExchangePage, CaseStarterPage
} from "@/pages/tools/core";

// Tool Pages - Calculators
import { DeadlinesPage, SettlementPage, DiscoveryPage, StatuteOfLimitationsPage } from "@/pages/tools/calculators";

// Tool Pages - Documents
import { DocumentsPage, DemandLetterPage, DepositionOutlinePage, MotionPracticePage, MediationBriefPage } from "@/pages/tools/documents";

// Tool Pages - Analytics
import { AnalyticsNewPage, JudgeAnalyticsPage, AttorneyAnalyticsPage, VenueComparisonPage, PartyHistoryPage, PrecedentSearchPage } from "@/pages/tools/analytics";

// Tool Pages - Practice
import { 
  OpeningStatementPage, ClosingArgumentPage, TrialOrganizerPage,
  SettlementNegotiationPage, InsuranceReportPage, CaseSummaryPage,
  MarketingContentPage, GovernmentResponsePage
} from "@/pages/tools/practice";

// Tool Pages - Specialized
import LegalRequestCenter from "@/pages/tools/LegalRequestCenter";
import MedicalProviderIntel from "@/pages/tools/MedicalProviderIntel";
import DocumentScannerPage from "@/pages/tools/DocumentScannerPage";
import LitigationGPSPage from "@/pages/tools/LitigationGPSPage";
import FinancialEnginePage from "@/pages/tools/FinancialEnginePage";
import LegalFinancingToolPage from "@/pages/tools/LegalFinancingPage";
import CompanezePage from "@/pages/tools/CompanezePage";
import PartnerPortalPage from "@/pages/tools/PartnerPortalPage";
import DamagesCalculatorPage from "@/pages/tools/DamagesCalculatorPage";
import FraudAnalyticsPage from "@/pages/tools/FraudAnalyticsPage";
import CourtFormsPage from "@/pages/tools/CourtFormsPage";
import TripleShieldPage from "@/pages/tools/TripleShieldPage";
import LiveInvestigatorPage from "@/pages/tools/LiveInvestigatorPage";
import ExpertWitnessPage from "@/pages/tools/ExpertWitnessPage";
import InsuranceInvestigationPage from "@/pages/tools/InsuranceInvestigationPage";
import CaseWorkspacePage from "@/pages/tools/CaseWorkspacePage";
import CourtFilingCyclesPage from "@/pages/tools/CourtFilingCyclesPage";
import CourtIntelligencePage from "@/pages/tools/CourtIntelligencePage";

// Service Network Pages
import {
  ServicesHubPage, AttorneyNetworkPage, ExpertNetworkPage,
  InvestigationServicesPage, InsuranceAnalyticsPage, ParalegalNetworkPage,
  CourierServicesPage, LegalFinancingPage, MarketingServicesPage
} from "@/pages/services";

// Intelligence Flywheel Pages
import { XAIPredictorPage, AcquisitionIntelPage, DataContributionsPage } from "@/pages/intelligence";

// Role Guards
import { RequireRole, DemoRestricted } from "@/components/shared/RoleGuard";

// Role-Based Dashboards
import { AdminDashboard, DemoDashboard, CompanyDashboard } from "@/pages/dashboards";
import CaseDashboardPage from "@/pages/dashboards/CaseDashboardPage";
// Insurance Section
import InsuranceSectionPage from "@/pages/tools/InsuranceSectionPage";
import CarrierScorecardPage from "@/pages/tools/CarrierScorecardPage";

// ============ PROTECTED ROUTE ============

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F8FAFC' }}>
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: '#3B82F6' }} />
          <p style={{ color: '#475569' }}>Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  return children;
};

// ============ AUTH CALLBACK ============

const AuthCallback = () => {
  const navigate = useNavigate();
  const { handleOAuthCallback } = useAuth();
  const hasProcessed = useRef(false);
  
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    
    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        try {
          await handleOAuthCallback(sessionIdMatch[1]);
          window.history.replaceState(null, '', window.location.pathname);
          navigate('/platform/dashboard', { replace: true });
        } catch (error) {
          console.error('OAuth callback error:', error);
          navigate('/login?error=auth_failed', { replace: true });
        }
      } else {
        navigate('/login', { replace: true });
      }
    };
    
    processAuth();
  }, [navigate, handleOAuthCallback]);
  
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#F8FAFC' }}>
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: '#3B82F6' }} />
        <p style={{ color: '#475569' }}>Completing sign in...</p>
      </div>
    </div>
  );
};

// ============ ROLE-BASED DASHBOARD ============

const RoleDashboard = () => {
  const { user } = useAuth();
  const role = user?.role;
  
  if (role === "admin") return <AdminDashboard />;
  if (role === "demo") return <DemoDashboard />;
  if (role === "company") return <CompanyDashboard />;
  return <DashboardPage />;
};

// ============ TOOL ROUTER ============

const ToolRouter = () => {
  const { tool } = useParams();
  
  const toolMap = {
    // Premium Intelligence
    'lytix': LytixLedgerPage,
    'investigator': InvestigatorPage,
    'litix-wizard': LitixWizardPage,
    'case-starter': CaseStarterPage,
    // Trial Preparation
    'opening': OpeningStatementPage,
    'closing': ClosingArgumentPage,
    'trial-organizer': TrialOrganizerPage,
    'deposition': DepositionOutlinePage,
    'motion': MotionPracticePage,
    // Settlement Suite
    'settlement-negotiation': SettlementNegotiationPage,
    'mediation': MediationBriefPage,
    'demand-letter': DemandLetterPage,
    'insurance-report': InsuranceReportPage,
    // Marketing & Government
    'marketing': MarketingContentPage,
    'government': GovernmentResponsePage,
    // Case Management
    'case-summary': CaseSummaryPage,
    'client-intake': ClientIntakePage,
    // Existing Tools
    'secrets': SecretsManagerPage,
    'strategy': StrategyPatternPage,
    'watchlist': WatchlistPage,
    'analytics': AnalyticsNewPage,
    'judges': JudgeAnalyticsPage,
    'attorneys': AttorneyAnalyticsPage,
    'parties': PartyHistoryPage,
    'precedent': PrecedentSearchPage,
    'precedent-finder': PrecedentFinderPage,
    'trend-analyzer': TrendAnalyzerPage,
    // Intelligence Flywheel
    'xai-predictor': XAIPredictorPage,
    'acquisition-intel': AcquisitionIntelPage,
    'data-contributions': DataContributionsPage,
    // Attorney Exchange
    'attorney-exchange': AttorneyExchangePage,
    // Calculators & Utilities
    'venue': VenueComparisonPage,
    'sol': StatuteOfLimitationsPage,
    'deadlines': DeadlinesPage,
    'settlement': SettlementPage,
    'documents': DocumentsPage,
    'discovery': DiscoveryPage,
    // Core Features
    'case-builder': CaseBuilderPage,
    'state-bar': StateBarPage,
    'intelligence': CaseIntelligencePage,
    'legal-data': LegalDataHubPage,
    // Specialized Tools
    'request-center': LegalRequestCenter,
    'medical-intel': MedicalProviderIntel,
    // Service Networks
    'services-hub': ServicesHubPage,
    'attorney-network': AttorneyNetworkPage,
    'expert-network': ExpertNetworkPage,
    'investigation-services': InvestigationServicesPage,
    'insurance-analytics': InsuranceAnalyticsPage,
    'paralegal-network': ParalegalNetworkPage,
    'courier-services': CourierServicesPage,
    'marketing-services': MarketingServicesPage,
    // Insurance Section
    'insurance': InsuranceSectionPage,
    'carrier-scorecard': CarrierScorecardPage,
    // Phase 2 — Intelligence & Financial
    'litigation-gps': LitigationGPSPage,
    'financial-engine': FinancialEnginePage,
    'legal-financing': LegalFinancingToolPage,
    'companeze': CompanezePage,
    'partner-portal': PartnerPortalPage,
    // Phase 2 — Damages & Fraud
    'damages-calculator': DamagesCalculatorPage,
    'fraud-analytics': FraudAnalyticsPage,
    'court-forms': CourtFormsPage,
    'triple-shield': TripleShieldPage,
    'live-investigator': LiveInvestigatorPage,
    'expert-witness': ExpertWitnessPage,
    'insurance-investigation': InsuranceInvestigationPage,
    'case-workspace': CaseWorkspacePage,
    'court-filing-cycles': CourtFilingCyclesPage,
    'court-intelligence': CourtIntelligencePage,
  };
  
  const Component = toolMap[tool] || ToolsPage;
  return <ErrorBoundary><Component /></ErrorBoundary>;
};

// ============ APP ROUTER ============

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return <AppContent />;
}

// ============ APP CONTENT ============

function AppContent() {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register' || location.pathname === '/auth/callback';
  
  return (
    <>
      {!isHomePage && !isAuthPage && !location.pathname.startsWith('/platform') && <Navbar />}
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/intake/:code" element={<ClientIntakePage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/methodology" element={<MethodologyPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/solutions" element={<SolutionsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/savings" element={<SavingsPage />} />
        <Route path="/account-terms" element={<AccountTermsPage />} />
        
        {/* Tools & Analytics (protected) */}
        <Route path="/dashboard" element={<ProtectedRoute><RoleDashboard /></ProtectedRoute>} />
        <Route path="/case/:caseId" element={<ProtectedRoute><CaseDashboardPage /></ProtectedRoute>} />
        <Route path="/scanner" element={<ProtectedRoute><DocumentScannerPage /></ProtectedRoute>} />
        <Route path="/tools" element={<ProtectedRoute><ToolsPage /></ProtectedRoute>} />
        <Route path="/tools/:tool" element={<ProtectedRoute><ToolRouter /></ProtectedRoute>} />
        <Route path="/tools/:tool/new" element={<ProtectedRoute><ToolRouter /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute><ReportsListPage /></ProtectedRoute>} />
        <Route path="/reports/:id" element={<ProtectedRoute><ReportViewPage /></ProtectedRoute>} />
        <Route path="/documents" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
        
        {/* Platform Pages (protected) */}
        <Route path="/platform/dashboard" element={<ProtectedRoute><PlatformDashboardPage /></ProtectedRoute>} />
        <Route path="/platform/cases" element={<ProtectedRoute><PlatformCasesPage /></ProtectedRoute>} />
        <Route path="/platform/documents" element={<ProtectedRoute><PlatformDocumentsPage /></ProtectedRoute>} />
        <Route path="/platform/profiles" element={<ProtectedRoute><PlatformProfilesPage /></ProtectedRoute>} />
        <Route path="/platform/settlements" element={<ProtectedRoute><PlatformSettlementsPage /></ProtectedRoute>} />
        <Route path="/platform/applications" element={<ProtectedRoute><PlatformApplicationsPage /></ProtectedRoute>} />
        <Route path="/platform/billing" element={<ProtectedRoute><PlatformBillingPage /></ProtectedRoute>} />
        <Route path="/platform/admin" element={<ProtectedRoute><RequireRole roles={["admin"]}><PlatformAdminPage /></RequireRole></ProtectedRoute>} />
        
        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

// ============ APP ============

function App() {
  return (
    <div className="min-h-screen" style={{ 
      background: 'linear-gradient(180deg, #f0f7ff 0%, #ffffff 30%, #f8fbff 100%)'
    }}>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
